import { c as _c } from "react/compiler-runtime";
import { datadogRum } from '@datadog/browser-rum';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef } from 'react';
import type { TableOfContentsHeadingProps } from '../types';
type Props = {
  activeId: string;
  headings: TableOfContentsHeadingProps[];
  setActiveId: Dispatch<SetStateAction<string>>;
  tocId: string;
};
type Entry = {
  isIntersecting: boolean;
  intersectionRatio: number;
  target: Element;
};
export function useHeadingsObserver(t0) {
  const $ = _c(11);
  const {
    activeId,
    headings,
    setActiveId,
    tocId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    current: intersectionRatios
  } = useRef(t1);
  const scrollTrackingIsEnabled = useRef(true);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      scrollTrackingIsEnabled.current = true;
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const reenableScrollTracking = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      scrollTrackingIsEnabled.current = false;
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const pauseScrollTracking = t3;
  let t4;
  if ($[3] !== activeId || $[4] !== headings || $[5] !== setActiveId || $[6] !== tocId) {
    t4 = () => {
      const tableOfContentElement = document.getElementById(tocId);
      let observer;
      if (tableOfContentElement) {
        const handleObserver = entries => {
          entries.forEach(entry => {
            const sectionId = entry.target.getElementsByTagName("h2")[0].id;
            const intersectionRatio = entry.intersectionRatio;
            intersectionRatios[sectionId] = intersectionRatio;
          });
          if (scrollTrackingIsEnabled.current) {
            let maxIntersectionRatio = activeId ? intersectionRatios[activeId] : 0;
            let mostVisibleSectionId = activeId;
            Object.entries(intersectionRatios).forEach(t5 => {
              const [sectionId_0, intersectionRatio_0] = t5;
              if (intersectionRatio_0 > maxIntersectionRatio) {
                maxIntersectionRatio = intersectionRatio_0;
                mostVisibleSectionId = sectionId_0;
              }
            });
            if (maxIntersectionRatio > 0) {
              setActiveId(mostVisibleSectionId);
            }
          }
        };
        observer = new IntersectionObserver(handleObserver, {
          root: null,
          threshold: Array.from({
            length: 11
          }, _temp)
        });
        const elements = headings.map(_temp2);
        elements.forEach(el => {
          let sectionElement;
          if (el && observer) {
            let iterator = el;
            do {
              iterator = iterator?.parentElement;
            } while (iterator && !iterator.className.includes("table-of-contents-section"));
            if (!iterator) {
              datadogRum.addError(new Error("Section not found"), {
                message: `Section for element with ID: ${el.id} and Class: ${el.className} not found`
              });
            }
            sectionElement = iterator;
            if (sectionElement) {
              observer.observe(sectionElement);
            }
          }
        });
        reenableScrollTracking();
        return () => {
          observer?.disconnect();
        };
      }
    };
    $[3] = activeId;
    $[4] = headings;
    $[5] = setActiveId;
    $[6] = tocId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== headings) {
    t5 = [headings];
    $[8] = headings;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      pauseScrollTracking,
      reenableScrollTracking
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function _temp2(t0) {
  const {
    id
  } = t0;
  return document.getElementById(id);
}
function _temp(_, i) {
  return i * 0.1;
}